import { useCallback, useEffect, useRef } from 'react';
import { Card, Col, Divider, Empty, Row, Table } from 'antd';

import { UserRole } from '../../../enum';
import { isMobile } from '../../../helpers/utils.helper';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { useAbortOnUnmount } from '../../../hooks/useAbortOnUnmount.hook';
import { useCapturePostHog } from '../../../hooks/useCapturePostHog';
import { useSetPageTitle } from '../../../hooks/useSetPageTitle';
import { getOrganizationProducts } from '../../../service/tableSlice';
import { fetchAdminOrganizations } from '../admin-organization/adminOrganizationSlice';
import { fetchAllSites } from '../admin-sites/adminSitesSlice';
import { DashboardBarChartComponent } from '../program-dashboard/charts/dashboard-bar-chart';
import { DashboardChurroChartComponent } from '../program-dashboard/charts/dashboard-churro-chart';
import { DowHourChartComponent } from '../program-dashboard/charts/dow-hour-chart';
import { InfoPopover } from '../program-dashboard/components/InfoPopover';
import { ProgramDashboardFilterForm } from '../program-dashboard/components/program-dashboard-filter-form';
import { CheckInDayAndTimeModal } from '../program-dashboard/modals/check-in-day-and-time-modal';
import { CheckInsModal } from '../program-dashboard/modals/check-ins-modal';
import { IncompleteSubmissionsModal } from '../program-dashboard/modals/incomplete-submissions-modal';
import { InterventionsModal } from '../program-dashboard/modals/interventions-modal';
import { DASHBOARD_MODAL, showDashboardModal } from '../program-dashboard/modals/modalDashboardSlice';
import { NewClientsModal } from '../program-dashboard/modals/new-clients-modal';
import { PopulationDistributionModal } from '../program-dashboard/modals/population-distribution-modal';
import { RedirectModal } from '../program-dashboard/modals/redirect-modal';
import { TimeInRiskModal } from '../program-dashboard/modals/time-in-risk-modal';
import { TotalClientModal } from '../program-dashboard/modals/total-client-modal';
import {
  getBarData,
  getInterventionsLogged,
  getPercentageBarData,
  getTotalIncompleteSubmissions,
  interventionColumns,
  useFiltersInfo,
} from '../program-dashboard/program-dashboard.helper';
import { sitesColumns } from './program-dashboard.helper';
import {
  handleParamsChange,
  loadCheckInDetails,
  loadDashboard,
  loadPopulationDetails,
  loadTotalClientDetails,
} from './programDashboardOLHSlice';

const ProgramDashboardOLHComponent = () => {
  const currentUser = useAppSelector((state) => state.authSlice.currentUser);
  const isAdmin = currentUser.role === UserRole.ADMIN;
  useSetPageTitle(`${isAdmin ? 'OLH ' : ''}Program dashboard`);
  const dispatch = useAppDispatch();
  const trackAbort = useAbortOnUnmount();
  const loadPromisesRef = useRef<{ abort: VoidFunction }[]>([]);
  const { capturePostHogCustomEvent } = useCapturePostHog();
  const modal = useAppSelector((state) => state.modalDashboardSlice.modal);
  const dashboardLoading = useAppSelector((state) => state.programDashboardOLHSlice.loadingDashboard);
  const programDashboardParams = useAppSelector((state) => state.programDashboardOLHSlice.programDashboardParams);
  const programDashboardData = useAppSelector((state) => state.programDashboardOLHSlice.programDashboardData);
  const filtersInfo = useFiltersInfo(programDashboardData.previousParams);
  const [interventionsLoggedSum, interventionsLoggedData] = getInterventionsLogged(
    programDashboardData.client_activity_trends.interventions,
  );

  const incomplete_submissions = getTotalIncompleteSubmissions(programDashboardData.issues.incomplete_submissions);

  const check_ins = useAppSelector((state) => state.programDashboardOLHSlice.check_ins);
  const checkInsSum = check_ins?.reduce((acc, c) => acc + c.total, 0);
  const checkInLoading = useAppSelector((state) => state.programDashboardOLHSlice.loadingCheckIn);

  const total_clients_details = useAppSelector((state) => state.programDashboardOLHSlice.total_clients_details);
  const totalClientLoading = useAppSelector((state) => state.programDashboardOLHSlice.loadingTotalClient);

  const populationLoading = useAppSelector((state) => state.programDashboardOLHSlice.loadingPopulation);
  const populationData = useAppSelector((state) => state.programDashboardOLHSlice.populationData);
  const ageDistributionData = getPercentageBarData(populationData.age_distribution);
  const primaryDiagnosisData = getPercentageBarData(populationData.primary_diagnosis_distribution);
  const genderDistributionData = getPercentageBarData(populationData.gender_distribution);
  const cafRcmpDistributionData = getBarData(populationData.caf_rcmp_distribution);
  const isProgramAdministration = currentUser.role === UserRole.PROGRAM_ADMINISTRATION;

  const check_in_distribution_by_dow_hour = useAppSelector(
    (state) => state.programDashboardOLHSlice.programDashboardData.check_in_distribution_by_dow_hour,
  );

  const abortLoadPromises = useCallback(() => {
    loadPromisesRef.current.forEach((promise) => promise.abort());
  }, []);

  const loadProgramDashboardData = useCallback(() => {
    abortLoadPromises();
    loadPromisesRef.current = [
      dispatch(loadDashboard()),
      dispatch(loadPopulationDetails()),
      dispatch(loadTotalClientDetails()),
      dispatch(loadCheckInDetails()),
    ];
  }, [abortLoadPromises, dispatch]);

  useEffect(() => {
    trackAbort(dispatch(getOrganizationProducts()));
    if (
      currentUser.role === UserRole.ADMIN ||
      currentUser.role === UserRole.ORGANIZATION ||
      currentUser.role === UserRole.PROGRAM_ADMINISTRATION
    ) {
      trackAbort(dispatch(fetchAllSites()));
    }
    if (currentUser.role === UserRole.ADMIN) {
      trackAbort(dispatch(fetchAdminOrganizations()));
    } else {
      loadProgramDashboardData();
    }

    return () => {
      abortLoadPromises();
    };
  }, [currentUser.role, currentUser.user_id, dispatch, trackAbort, abortLoadPromises, loadProgramDashboardData]);

  const handleDashboardModal = (modal: DASHBOARD_MODAL) => {
    dispatch(showDashboardModal(modal));
    capturePostHogCustomEvent(`ProgramDashboardOLHComponent clicked ${modal} modal`);
  };

  return (
    <div className={!isMobile ? 'homeContainer' : 'homeContainerMobile'}>
      <Card
        className="cardShadowEffect"
        title={
          <span className="parrotHeader">Program dashboard{currentUser.role === UserRole.ADMIN ? ' OLH' : ''}</span>
        }
      >
        <ProgramDashboardFilterForm
          onFinish={() => {
            loadProgramDashboardData();
            capturePostHogCustomEvent('ProgramDashboardOLHComponent load dashboard data');
          }}
          submitLoading={dashboardLoading}
          programDashboardParams={programDashboardParams}
          isOlhDashboard
          onParamsChange={(params) => dispatch(handleParamsChange(params))}
        />
        <Card variant="borderless" style={{ marginTop: 16, backgroundColor: '#FAFAFA', marginRight: 0, marginLeft: 0 }}>
          <Row gutter={12}>
            <Col xs={24} sm={8} md={6} lg={6} xl={3}>
              <Row style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Card
                    loading={checkInLoading}
                    className={isProgramAdministration ? 'tile' : 'clickable'}
                    onClick={() => {
                      if (!isProgramAdministration) handleDashboardModal(DASHBOARD_MODAL.CHECK_INS);
                    }}
                  >
                    <InfoPopover
                      title="Total check-ins"
                      content={<div>Total count of client check-ins during the selected date range.</div>}
                    />
                    <p className="largeStat">{checkInsSum ? checkInsSum : '-'}</p>
                  </Card>
                  {modal === DASHBOARD_MODAL.CHECK_INS && <CheckInsModal data={check_ins} filtersInfo={filtersInfo} />}
                </Col>
              </Row>
              <Row gutter={5} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Card
                    loading={totalClientLoading}
                    className={isProgramAdministration ? 'tile' : 'clickable'}
                    onClick={() => {
                      if (!isProgramAdministration) handleDashboardModal(DASHBOARD_MODAL.TOTAL_CLIENT);
                    }}
                  >
                    <InfoPopover
                      title="Total clients"
                      content={
                        <div>
                          Number of clients that were created and active for at least one day of the date range.
                        </div>
                      }
                    />
                    <p className="largeStat">{total_clients_details?.length ?? '-'}</p>
                  </Card>
                  <TotalClientModal data={total_clients_details} filtersInfo={filtersInfo} />
                </Col>
              </Row>
              <Row gutter={2} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Card
                    loading={populationLoading}
                    className={isProgramAdministration ? 'tile' : 'clickable'}
                    onClick={() => {
                      if (!isProgramAdministration) handleDashboardModal(DASHBOARD_MODAL.NEW_CLIENTS);
                    }}
                  >
                    <InfoPopover
                      title="New clients"
                      content={<div>Number of clients created in the selected date range.</div>}
                    />
                    <p className="largeStat">{populationData.new_clients?.length ?? '-'}</p>
                  </Card>
                  <NewClientsModal data={populationData.new_clients} filtersInfo={filtersInfo} />
                </Col>
              </Row>
              <Row style={{ marginTop: 20 }}>
                <Col span={24}>
                  <Card className="cardShadowEffect">
                    <span className="infoTitle">
                      <h2 style={{ marginBottom: '-10px' }}>Issues</h2>
                    </span>
                    <Divider />
                    <Row>
                      <Col span={24}>
                        <Card
                          loading={dashboardLoading}
                          className={isProgramAdministration ? 'tile' : 'clickable'}
                          onClick={() => {
                            if (!isProgramAdministration) {
                              handleDashboardModal(DASHBOARD_MODAL.NO_ACTIVITY);
                            }
                          }}
                        >
                          <InfoPopover
                            title="Clients with no activity last 120+ days"
                            content={
                              <div>
                                Total # of active clients who’s last check in was more than 120 days ago.
                                <br />
                                <br />
                                Note: this # is calculated as of today; the selected date range does not apply.
                              </div>
                            }
                          />
                          <p className="largeStat">
                            {programDashboardData.issues.number_of_clients_with_no_activity ?? '-'}
                          </p>
                        </Card>
                        {modal === DASHBOARD_MODAL.NO_ACTIVITY && <RedirectModal />}
                      </Col>
                    </Row>
                    <Row style={{ marginTop: 16 }}>
                      <Col span={24}>
                        <Card
                          loading={populationLoading}
                          className={isProgramAdministration ? 'tile' : 'clickable'}
                          onClick={() => {
                            if (!isProgramAdministration) handleDashboardModal(DASHBOARD_MODAL.NOT_ONBOARDED);
                          }}
                        >
                          <InfoPopover
                            title="Clients who did not complete onboarding"
                            content={
                              <div>
                                Total # of created clients that have not checked in yet. These clients may have had
                                trouble logging in or did not complete the onboarding process.
                                <br />
                                <br />
                                Note: this # is calculated as of today; the selected date range does not apply.
                              </div>
                            }
                          />
                          <p className="largeStat">{populationData.clients_who_did_not_onboard?.length ?? '-'}</p>
                        </Card>
                      </Col>
                    </Row>
                    <Row style={{ marginTop: 16 }}>
                      <Col span={24}>
                        <Card
                          loading={dashboardLoading}
                          className={incomplete_submissions === 0 ? 'tile' : 'clickable'}
                          onClick={() => {
                            handleDashboardModal(DASHBOARD_MODAL.INCOMPLETE_SUBMISSIONS);
                          }}
                        >
                          <InfoPopover
                            title="Incomplete check-ins"
                            content={
                              <div>
                                Total # of check-ins where at least 1 question was answered, but the client did not
                                complete the check-in.
                              </div>
                            }
                          />
                          <p className="largeStat">{incomplete_submissions}</p>
                        </Card>
                        {modal === DASHBOARD_MODAL.INCOMPLETE_SUBMISSIONS && (
                          <IncompleteSubmissionsModal
                            data={programDashboardData.issues.incomplete_submissions}
                            filtersInfo={filtersInfo}
                          />
                        )}
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </Col>
            <Col xs={24} sm={16} md={18} lg={18} xl={11}>
              <Row style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Card className="cardShadowEffect" title="Client activity trends" extra={filtersInfo}>
                    <Row gutter={10} style={{ marginTop: 16 }}>
                      <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Card
                          loading={dashboardLoading}
                          className={isProgramAdministration ? 'tile' : 'clickable'}
                          onClick={() => {
                            if (!isProgramAdministration) handleDashboardModal(DASHBOARD_MODAL.INTERVENTIONS);
                          }}
                          style={{ minHeight: 345 }}
                        >
                          <span className="infoTitle">
                            <h2>Clinician notes logged</h2>
                          </span>
                          {interventionsLoggedSum === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 60 }} />
                          ) : (
                            <>
                              <p className="extraSpace largeStat">{interventionsLoggedSum}</p>
                              <Card loading={dashboardLoading} className="interventionCard">
                                <Table
                                  rowKey="name"
                                  dataSource={interventionsLoggedData}
                                  columns={interventionColumns}
                                  pagination={false}
                                />
                              </Card>
                            </>
                          )}
                        </Card>
                        <InterventionsModal
                          data={programDashboardData.client_activity_trends.interventions}
                          filtersInfo={filtersInfo}
                        />
                        <Card
                          loading={dashboardLoading}
                          style={{ marginTop: 16 }}
                          className={isProgramAdministration ? 'tile' : 'clickable'}
                          onClick={() => {
                            if (!isProgramAdministration) handleDashboardModal(DASHBOARD_MODAL.TIME_IN_RISK);
                          }}
                        >
                          <InfoPopover
                            info
                            title={<h2>Time in risk status</h2>}
                            content={
                              <div>
                                This chart shows the percentage of time that clients were in each risk status on average
                                during the selected date range.
                              </div>
                            }
                          />
                          <DashboardChurroChartComponent
                            data={programDashboardData.client_activity_trends.status_distribution}
                          />
                        </Card>
                        <TimeInRiskModal
                          data={programDashboardData.client_activity_trends.status_distribution}
                          filtersInfo={filtersInfo}
                        />
                      </Col>
                      <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Card loading={dashboardLoading} className="tile" style={{ minHeight: 345 }}>
                          <span className="infoTitle">
                            <h2>Sites</h2>
                          </span>
                          {interventionsLoggedSum === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 60 }} />
                          ) : (
                            <>
                              <Card loading={dashboardLoading} className="">
                                <Table
                                  rowKey="name"
                                  dataSource={programDashboardData.site_distribution}
                                  columns={sitesColumns}
                                  pagination={false}
                                />
                              </Card>
                            </>
                          )}
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={10} style={{ marginBottom: 16 }}>
              <Row style={{ marginTop: 16 }}>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Card
                    loading={dashboardLoading}
                    className="clickable"
                    style={{ minHeight: 483 }}
                    onClick={() => handleDashboardModal(DASHBOARD_MODAL.CHECKIN_DAY_AND_TIME)}
                  >
                    <InfoPopover
                      info
                      title={<h2>Check-in day and time</h2>}
                      content={
                        <div>
                          The distribution of check-in times by hour of day and day of week.
                          <br />
                          <br />
                          See the hours of the day and days of the week during which clients are most often checking in.
                          <br />
                          <br />
                          Larger circles represent more check-ins for that day of the week at that hour.
                        </div>
                      }
                    />
                    <div style={{ marginBottom: 8 }}>{filtersInfo}</div>
                    <DowHourChartComponent data={check_in_distribution_by_dow_hour} />
                  </Card>
                  <CheckInDayAndTimeModal data={check_in_distribution_by_dow_hour} filtersInfo={filtersInfo} />
                </Col>
              </Row>
              <Row style={{ marginTop: 16 }}>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Card
                    style={{ minHeight: 498 }}
                    className="cardShadowEffect"
                    title="Population breakdown"
                    extra={filtersInfo}
                  >
                    <Row gutter={10} style={{ marginTop: 16 }}>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Card
                          loading={populationLoading}
                          className="clickable"
                          onClick={() => handleDashboardModal(DASHBOARD_MODAL.PRIMARY_DIAGNOSIS)}
                        >
                          Primary diagnosis
                          <DashboardBarChartComponent data={primaryDiagnosisData} rotate={true} />
                        </Card>
                        <PopulationDistributionModal
                          open={modal === DASHBOARD_MODAL.PRIMARY_DIAGNOSIS}
                          data={populationData.primary_diagnosis_distribution}
                          filtersInfo={filtersInfo}
                          data_key="primary_diagnosis"
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Card
                          loading={populationLoading}
                          className="clickable"
                          onClick={() => handleDashboardModal(DASHBOARD_MODAL.AGE_DISTRIBUTION)}
                        >
                          Age
                          <DashboardBarChartComponent data={ageDistributionData} rotate={false} />
                        </Card>
                        <PopulationDistributionModal
                          open={modal === DASHBOARD_MODAL.AGE_DISTRIBUTION}
                          data={populationData.age_distribution}
                          filtersInfo={filtersInfo}
                          data_key="age_group"
                        />
                      </Col>
                    </Row>
                    <Row gutter={10} style={{ marginTop: 16 }}>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Card
                          loading={populationLoading}
                          className="clickable"
                          onClick={() => handleDashboardModal(DASHBOARD_MODAL.CAF_RCMP)}
                        >
                          Military association
                          <DashboardBarChartComponent data={cafRcmpDistributionData} rotate={false} />
                        </Card>
                        <PopulationDistributionModal
                          open={modal === DASHBOARD_MODAL.CAF_RCMP}
                          data={populationData.caf_rcmp_distribution}
                          filtersInfo={filtersInfo}
                          data_key="military_association"
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Card
                          loading={populationLoading}
                          className="clickable"
                          onClick={() => handleDashboardModal(DASHBOARD_MODAL.GENDER)}
                        >
                          Gender
                          <DashboardBarChartComponent data={genderDistributionData} rotate={true} />
                        </Card>
                        <PopulationDistributionModal
                          open={modal === DASHBOARD_MODAL.GENDER}
                          data={populationData.gender_distribution}
                          filtersInfo={filtersInfo}
                          data_key="gender"
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>
      </Card>
    </div>
  );
};

export default ProgramDashboardOLHComponent;
